import { Mail, Phone, Undo2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { Button } from './ui/button';

/** Mirrors src/application/approvals/act-now.ts. */
interface Actions { emailMode: 'approve' | 'autonomous' | 'mixed'; voice: { mode: 'off' | 'test' | 'live'; phone: string | null } }
interface EmailNow {
  status: 'approved' | 'needs_approval' | 'nothing_due'; approvalId: string | null; revision: number | null;
  sendsAt: string | null; to: string[]; reason: string | null; existing: boolean;
}
interface Dispatch { status: 'executed' | 'waiting' | 'stale' | 'uncertain' | 'failed'; reasons: string[] }
interface CallNow { callId: string; mode: 'test' | 'live'; dialed: number; deferred: number; failed: number; cancelled: number }

type EmailState =
  | { kind: 'idle' } | { kind: 'busy' }
  | { kind: 'counting'; id: string; revision: number; to: string; at: number }
  | { kind: 'sent'; to: string } | { kind: 'draft'; id: string; reason?: string } | { kind: 'note'; text: string; tone?: 'error' };
type CallState = { kind: 'idle' } | { kind: 'busy' } | { kind: 'calling'; id: string; text: string } | { kind: 'note'; text: string; tone?: 'error' };

const recipient = (to: string[]) => to.join(', ') || 'the billing contact';
const message = (error: unknown, fallback: string) => error instanceof Error ? error.message : fallback;

/** Why the call button is off, in the words the owner sees; null when a call can be placed. */
export function callBlock(voice: Actions['voice']): string | null {
  if (voice.mode === 'off') return 'Calls are off on this workspace';
  if (voice.mode === 'live' && !voice.phone) return 'No phone on file';
  return null;
}

/**
 * Owner actions on the customer page, for doing the next step on cue: Email now drafts the customer's due reminder through
 * the planner's path (Amy sends it after the Undo window, or a person approves it in Needs you), and Call now rings the
 * contact on file under the workspace's dial rules.
 */
export function ActNow({ customerId }: { customerId: string }) {
  const [actions, setActions] = useState<Actions | null>(null);
  const [email, setEmail] = useState<EmailState>({ kind: 'idle' });
  const [call, setCall] = useState<CallState>({ kind: 'idle' });
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let active = true;
    void api<Actions>(`/app-api/customers/${encodeURIComponent(customerId)}/actions`).then(a => { if (active) setActions(a); })
      .catch(() => { if (active) setActions(null); });
    return () => { active = false; };
  }, [customerId]);

  // The Undo countdown, then the send itself: the same "send now" an approver uses for an approved email.
  useEffect(() => {
    if (email.kind !== 'counting') return;
    const timer = window.setInterval(() => setNow(Date.now()), 500);
    const wait = Math.max(0, email.at - Date.now()) + 300;
    const fire = window.setTimeout(() => {
      void api<Dispatch>(`/app-api/approvals/${email.id}/send`, {}).then(result => {
        if (result.status === 'executed') setEmail({ kind: 'sent', to: email.to });
        else if (result.status === 'waiting') setEmail({ kind: 'note', text: `Approved. ${result.reasons[0] ?? 'It goes out at the next send window.'}` });
        else setEmail({ kind: 'note', tone: 'error', text: `Held back: ${result.reasons[0] ?? 'the facts changed before sending.'}` });
      }).catch(error => setEmail({ kind: 'note', tone: 'error', text: message(error, 'The email could not be sent.') }));
    }, wait);
    return () => { window.clearInterval(timer); window.clearTimeout(fire); };
  }, [email]);

  if (!actions) return null;
  const approve = actions.emailMode === 'approve';

  async function emailNow() {
    setEmail({ kind: 'busy' });
    try {
      const result = await api<EmailNow>(`/app-api/customers/${encodeURIComponent(customerId)}/email-now`, {});
      if (result.status === 'nothing_due') setEmail({ kind: 'note', text: result.reason ?? 'Nothing is due for this customer yet.' });
      else if (result.status === 'needs_approval') setEmail({ kind: 'draft', id: result.approvalId!, ...(result.reason ? { reason: result.reason } : {}) });
      else setEmail({ kind: 'counting', id: result.approvalId!, revision: result.revision!, to: recipient(result.to),
        at: result.sendsAt ? Date.parse(result.sendsAt) : Date.now() });
    } catch (error) { setEmail({ kind: 'note', tone: 'error', text: message(error, 'The email could not be drafted.') }); }
  }
  async function undo(state: Extract<EmailState, { kind: 'counting' }>) {
    try {
      await api(`/app-api/approvals/${state.id}/decisions`, { kind: 'undo', revision: state.revision, idempotencyKey: crypto.randomUUID() });
      setEmail({ kind: 'draft', id: state.id });
    } catch (error) { setEmail({ kind: 'note', tone: 'error', text: message(error, 'It was too late to undo.') }); }
  }
  async function callNow() {
    setCall({ kind: 'busy' });
    try {
      const result = await api<CallNow>('/app-api/voice/call-now', { customerId });
      const who = result.mode === 'test' ? 'the test line' : actions!.voice.phone ?? 'the contact on file';
      if (result.dialed) setCall({ kind: 'calling', id: result.callId, text: `Calling ${who} now` });
      else if (result.deferred) setCall({ kind: 'calling', id: result.callId, text: 'The call is queued and starts in a moment' });
      else setCall({ kind: 'note', tone: 'error', text: 'The call could not be placed.' });
    } catch (error) { setCall({ kind: 'note', tone: 'error', text: message(error, 'The call could not be placed.') }); }
  }

  const blocked = callBlock(actions.voice);
  const seconds = email.kind === 'counting' ? Math.max(0, Math.ceil((email.at - now) / 1000)) : 0;
  return <div className="act-now" aria-label="Act now">
    <div className="act-now-buttons">
      <Button size="sm" disabled={email.kind === 'busy' || email.kind === 'counting'} onClick={() => void emailNow()}>
        <Mail aria-hidden />{email.kind === 'busy' ? 'Writing…' : approve ? 'Draft email now' : 'Email now'}</Button>
      <Button size="sm" variant="outline" disabled={Boolean(blocked) || call.kind === 'busy'} title={blocked ?? undefined}
        onClick={() => void callNow()}><Phone aria-hidden />{call.kind === 'busy' ? 'Dialing…' : 'Call now'}</Button>
    </div>
    <div className="act-now-status" role="status" aria-live="polite">
      {email.kind === 'counting' && <span>{seconds > 0 ? `Email going out to ${email.to} in ${seconds} s` : `Sending to ${email.to}…`}
        {seconds > 0 && <Button size="sm" variant="ghost" onClick={() => void undo(email)}><Undo2 aria-hidden />Undo</Button>}</span>}
      {email.kind === 'sent' && <span>Sent to {email.to}.</span>}
      {email.kind === 'draft' && <span>{email.reason ? `Held for you: ${email.reason} ` : 'Draft ready for approval. '}
        <a href={`#approvals/${email.id}`}>Open the draft</a></span>}
      {email.kind === 'note' && <span className={email.tone === 'error' ? 'act-now-error' : undefined}>{email.text}</span>}
      {blocked && <span className="muted">{blocked}.</span>}
      {call.kind === 'calling' && <span>{call.text}. <a href={`#calls/${call.id}`}>Open the call</a></span>}
      {call.kind === 'note' && <span className={call.tone === 'error' ? 'act-now-error' : undefined}>{call.text}</span>}
    </div>
  </div>;
}
